// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Slide, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch } from 'Hooks';
import { createNewFolderThunk, updateFolderThunk } from 'Features/folderList/folderListThunk';
import stylesDef from './styles';

interface CreateFolderDialogProps {
  showDialog: boolean;
  title?: string;
  isEdit?: boolean;
  folderName?: string;
  folderId?: string;
  setShowDialog: (show: boolean) => void;
}

const Transition = React.forwardRef<unknown, TransitionProps>((props, ref) => <Slide ref={ref} direction="up" {...props} />);

const CreateFolderDialog = ({ showDialog, title = 'New Folder', isEdit = false, folderName, folderId, setShowDialog }: CreateFolderDialogProps) => {
  // Constants
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  // State Variables
  const [newFolderName, setNewFolderName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // useEffect
  useEffect(() => {
    if (isEdit && folderName) {
      setNewFolderName(folderName);
    } else {
      setNewFolderName('');
    }
  }, [isEdit, folderName, showDialog]);

  // Methods
  const handleFolderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewFolderName(e.target.value);
    setError('');
  };

  const handleCreateFolder = () => {
    // Validate folder name
    if (newFolderName.trim()) {
      // If editing and folderId is available rename the folder; otherwise, create a new folder
      if (isEdit && folderId) {
        renameFolder(folderId, newFolderName.trim());
      } else {
        createFolder(newFolderName.trim());
      }
    }
  };

  const createFolder = async (name: string) => {
    setLoading(true);
    // API call to create a new folder
    const result = await dispatch(createNewFolderThunk(name));
    if (!result.success) {
      const errorMessage = result.message === `Folder with name ${name} already exists` ? `${name} folder already exists` : 'Failed to create folder';
      setError(errorMessage);
      setLoading(false);
      return;
    }

    if (result.success) {
      // Reset the folder name and close the dialog
      setNewFolderName('');
      setShowDialog(false);
    }

    setLoading(false);
  };

  const renameFolder = async (id: string, name: string) => {
    setLoading(true);
    // API call to update the folder name
    const result = await dispatch(updateFolderThunk(id, name));

    if (result.success) {
      setNewFolderName('');
      setShowDialog(false);
    }

    setLoading(false);
  };

  return (
    <Dialog
      open={showDialog}
      onClose={() => setShowDialog(false)}
      TransitionComponent={Transition}
      keepMounted={false}
      sx={styles.dialog}
    >
      <Box sx={styles.titleSection}>
        <DialogTitle sx={styles.dialogTitle}>
          {title}
        </DialogTitle>
        <IconButton
          color="inherit"
          onClick={() => setShowDialog(false)}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={styles.dialogContent}>
        <Typography variant="body1" sx={styles.dialogText}>
          Please name your folder:
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Folder Name"
          placeholder="Enter folder name"
          variant="outlined"
          value={newFolderName}
          inputProps={{ 'aria-label': 'Folder Name' }}
          onChange={handleFolderNameChange}
          error={!!error}
          helperText={error}
          sx={styles.textField}
        />
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button onClick={() => setShowDialog(false)} sx={styles.actionButtons}>
          Cancel
        </Button>
        <Button
          onClick={handleCreateFolder}
          disabled={!newFolderName.trim() || loading}
          variant="contained"
          sx={styles.actionButtons}
        >
          {loading ? (
            <CircularProgress size={24} sx={styles.buttonProgress} />
          ) : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateFolderDialog.defaultProps = {
  title: 'New Folder',
  isEdit: false,
  folderName: '',
  folderId: '',
};

export default CreateFolderDialog;
